import React, { useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Loader2, ArrowRight } from 'lucide-react';
import { countryCodes } from '../data/mockData';

const ContactFormDialog = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
  const [errors, setErrors] = useState({ email: '', phone: '' });

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'email' || field === 'phone') setErrors(prev => ({ ...prev, [field]: '' }));
  };
  const handleEmailBlur = () => { if (formData.email && !validateEmail(formData.email)) setErrors(prev => ({ ...prev, email: 'Please enter a valid email' })); };
  const handlePhoneChange = (value) => {
    const d = value.replace(/\D/g, '');
    if (d.length <= 10) { handleChange('phone', d); if (d && d.length !== 10) setErrors(prev => ({ ...prev, phone: 'Must be 10 digits' })); else setErrors(prev => ({ ...prev, phone: '' })); }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = { email: '', phone: '' };
    let hasErrors = false;
    if (!validateEmail(formData.email)) { newErrors.email = 'Please enter a valid email'; hasErrors = true; }
    if (formData.phone.length !== 10) { newErrors.phone = 'Must be 10 digits'; hasErrors = true; }
    if (hasErrors) { setErrors(newErrors); return; }
    setIsSubmitting(true);
    setTimeout(() => {
      toast({ title: "Request Submitted!", description: "Our trademark experts will be in touch within 24 hours." });
      setIsSubmitting(false);
      setFormData({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
      setErrors({ email: '', phone: '' });
      onClose();
    }, 1500);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-testid="contact-form-dialog" className="max-w-[850px] p-0 gap-0 overflow-hidden border border-white/10 bg-[#093336] rounded-2xl shadow-2xl">
        <div className="flex flex-col md:flex-row">
          <div className="hidden md:block w-[40%] relative overflow-hidden rounded-l-2xl">
            <img
              src="https://images.unsplash.com/photo-1769038947088-62455c949efc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHx0cmFkZW1hcmslMjBsZWdhbCUyMGRvY3VtZW50JTIwc2lnbmluZyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzYyNzc2NjR8MA&ixlib=rb-4.1.0&q=85"
              alt="Signing trademark documents"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#041B1C]/80 via-[#041B1C]/30 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 z-10">
              <h3 className="text-lg font-bold text-[#F0F4F4] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Protect Your Brand Name</h3>
              <div className="space-y-2">
                {['Licensed USPTO Attorneys', 'Filed in 48 Hours', 'Starts at $49'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-[#F0F4F4]/70">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#A5BF23]" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full md:w-[60%] p-7">
            <div className="mb-5">
              <h2 className="text-xl font-bold text-[#F0F4F4] mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Schedule Your Free Review</h2>
              <p className="text-sm text-[#A0B2B2]">A trademark specialist will connect with you shortly.</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label htmlFor="dialog-name" className="text-[#A0B2B2] text-xs font-medium">Full Name *</Label>
                <Input id="dialog-name" data-testid="dialog-name-input" placeholder="Enter your full name" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} required className="h-10 bg-[#041B1C] border-white/10 text-[#F0F4F4] placeholder:text-[#A0B2B2]/50 focus:border-[#A5BF23] focus:ring-1 focus:ring-[#A5BF23] rounded-xl mt-1" />
              </div>
              <div>
                <Label htmlFor="dialog-email" className="text-[#A0B2B2] text-xs font-medium">Email *</Label>
                <Input id="dialog-email" data-testid="dialog-email-input" type="email" placeholder="your@email.com" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} onBlur={handleEmailBlur} required className={`h-10 bg-[#041B1C] text-[#F0F4F4] placeholder:text-[#A0B2B2]/50 rounded-xl mt-1 ${errors.email ? 'border-red-500' : 'border-white/10'} focus:border-[#A5BF23] focus:ring-1 focus:ring-[#A5BF23]`} />
                {errors.email && <p className="text-red-400 text-xs mt-0.5">{errors.email}</p>}
              </div>
              <div>
                <Label htmlFor="dialog-phone" className="text-[#A0B2B2] text-xs font-medium">Phone *</Label>
                <div className="flex gap-2 mt-1">
                  <Select value={formData.countryCode} onValueChange={(v) => handleChange('countryCode', v)}>
                    <SelectTrigger data-testid="dialog-country-select" className="w-[100px] h-10 bg-[#041B1C] border-white/10 text-[#F0F4F4] rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-[300px] bg-[#093336] border-white/10">
                      {countryCodes.map((item) => (<SelectItem key={item.code} value={item.code} className="text-[#F0F4F4]"><span className="flex items-center gap-2"><span>{item.flag}</span><span>{item.code}</span></span></SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Input id="dialog-phone" data-testid="dialog-phone-input" type="tel" placeholder="1234567890" value={formData.phone} onChange={(e) => handlePhoneChange(e.target.value)} required maxLength={10} className={`flex-1 h-10 bg-[#041B1C] text-[#F0F4F4] placeholder:text-[#A0B2B2]/50 rounded-xl ${errors.phone ? 'border-red-500' : 'border-white/10'} focus:border-[#A5BF23] focus:ring-1 focus:ring-[#A5BF23]`} />
                </div>
                {errors.phone && <p className="text-red-400 text-xs mt-0.5">{errors.phone}</p>}
              </div>
              <div>
                <Label htmlFor="dialog-desc" className="text-[#A0B2B2] text-xs font-medium">What do you need?</Label>
                <Textarea id="dialog-desc" data-testid="dialog-description-input" placeholder="e.g. Register my brand name with the USPTO" rows={2} value={formData.description} onChange={(e) => handleChange('description', e.target.value)} className="bg-[#041B1C] border-white/10 text-[#F0F4F4] placeholder:text-[#A0B2B2]/50 focus:border-[#A5BF23] focus:ring-1 focus:ring-[#A5BF23] resize-none rounded-xl mt-1" />
              </div>
              <Button type="submit" data-testid="dialog-submit-button" className="w-full bg-[#A5BF23] hover:bg-[#8A9F1D] text-[#041B1C] font-semibold h-11 rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_32px_rgba(165,191,35,0.3)]" disabled={isSubmitting}>
                {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</> : <>Submit Request <ArrowRight className="ml-2 w-4 h-4" /></>}
              </Button>
              <p className="text-[10px] text-[#A0B2B2]/40 text-center">By submitting, you agree to our Terms and Privacy Policy.</p>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactFormDialog;
